/**
 * Task-like wrappers modelled loosely on ember-concurrency's task modifiers.
 * `enqueueTask` serializes concurrent calls; `coalesceTask` folds them into the
 * run already in flight.
 *
 * Pick by asking what a second concurrent call should mean. For a read, running
 * it late is harmless and callers want the data, so enqueue. For a request that
 * moves money, the second call is a double-submit and must not reach the server,
 * so coalesce.
 */

/**
 * A task-like wrapper around a serial promise queue, modelled loosely on
 * ember-concurrency's `enqueue` task.
 *
 * `enqueueTask(task)` builds an internal queue and returns an object with a
 * `perform` method that forwards its arguments to `task`. Each `perform(...)`
 * runs only after the previous run has settled, so concurrent callers never
 * overlap. The promise returned to a caller settles with that run's own
 * result (errors propagate to the caller), while the internal chain swallows
 * rejections so later runs still execute.
 *
 * Reactive task state (e.g. `isRunning`, `isError` atoms) can be added to the
 * returned object later without changing call sites.
 */
/** The task fn this queue wraps: any async function. */
type AsyncTask = (...args: never[]) => Promise<unknown>;

export interface EnqueuedTask<TTask extends AsyncTask> {
  /** Forwards its arguments to the wrapped task, queued behind the previous run. */
  perform: (...args: Parameters<TTask>) => ReturnType<TTask>;
}

export function enqueueTask<TTask extends AsyncTask>(task: TTask): EnqueuedTask<TTask> {
  let tail: Promise<unknown> = Promise.resolve();

  return {
    perform(...args: Parameters<TTask>) {
      const result = tail.then(() => task(...args));
      tail = result.then(
        () => undefined,
        () => undefined,
      );
      // `result` is `Promise<Awaited<ReturnType<TTask>>>`, which is structurally
      // `ReturnType<TTask>`; assert it so callers get the task's own return type.
      return result as ReturnType<TTask>;
    },
  };
}

export interface CoalescedTask<TTask extends AsyncTask> {
  /**
   * Forwards its arguments to the wrapped task, unless a run is already in flight — then it returns
   * that run's promise instead of starting a second one.
   */
  perform: (...args: Parameters<TTask>) => ReturnType<TTask>;
}

/**
 * A task-like wrapper that collapses concurrent calls into the run already in flight. The
 * double-submit guard for requests that move money: a second click must not reach the server.
 *
 * `enqueueTask` cannot serve that purpose — it *serializes*, so a double-click still sends two
 * requests, just one after the other.
 *
 * The in-flight handle is set synchronously before the first `await`, so two calls in the same tick
 * behave correctly. A `useState` flag cannot do this: both handlers would read the same stale value and
 * both would proceed.
 *
 * **Why the extra caller shares the result instead of being refused.** Ember used
 * `task({ drop: true })`, whose extra call is cancelled and whose caller therefore never resumes. There
 * is no honest way to express "never resumes" with a promise: resolving `undefined` makes every
 * `Promise<void>` caller read the refusal as success — and these callers announce success right after
 * awaiting, so a refused cancel would report "Account scheduled to be canceled" for a request that
 * never left. Telling someone their account is cancelled when it is not is worse than the duplicate
 * request this exists to prevent.
 *
 * Sharing the promise keeps the guarantee that matters — one request reaches the server — while every
 * caller reports what actually happened, success or failure. The repeated side effects are idempotent
 * by construction: these snackbars carry fixed ids, and Pluma replaces a snackbar with an id already on
 * screen instead of stacking it, so the second confirmation is invisible.
 *
 * The trade-off to know about: a second call's *arguments* are ignored, because it joins a run that has
 * already started. That is right for a double-submit, which repeats the same arguments. Do not use this
 * to coalesce calls whose arguments genuinely differ.
 */
export function coalesceTask<TTask extends AsyncTask>(task: TTask): CoalescedTask<TTask> {
  let inFlight: Promise<unknown> | null = null;

  return {
    perform(...args: Parameters<TTask>) {
      const existing = inFlight;
      if (existing != null) {
        return existing as ReturnType<TTask>;
      }

      const run = task(...args);
      inFlight = run;
      // Release the slot once this run settles so the next call starts fresh. Both handlers swallow,
      // so this bookkeeping chain never raises an unhandled rejection; the error still reaches every
      // caller through `run` itself.
      const release = () => {
        if (inFlight === run) {
          inFlight = null;
        }
      };
      void run.then(release, release);

      return run as ReturnType<TTask>;
    },
  };
}
